import DownloadPopularityTimeSeriesButton from 'components/Button/DownloadPopularityTimeSeriesButton';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import EntityChartCard from './EntityChartCard';
import EntityChartDownload from './EntityChartDownload';
import GoogleEntityChart, { googleSeries } from './GoogleEntityChart';
import InstagramTimeSeries from 'components/DataVis/InstagramTimeSeries';
import PopularityEntityChart, {
    popularityDownloadSeries,
    popularitySeries,
} from './PopularityEntityChart';
import PopularityIcon from 'components/PopularityIcon';
import DefinitionTooltip from 'components/DefinitionTooltip';
import TikTokTimeSeries from 'components/DataVis/TikTokTimeSeries';
import WeeklyEntityChart, { weeklySeries } from './WeeklyEntityChart';
import { tooltips } from '@/copy';
import { useEntityPageContext } from './EntityContext';

/**
 * One card per source: the labelled card, its download button and its chart,
 * which always travel together. `chart` props reach the chart, and anything a
 * page needs to override on the chart goes through it.
 *
 * Google and the pop-index put the screenshot ref on a wrapping div rather
 * than on the chart, which is a DOM fact about the download button.
 */
export const GoogleChartCard = ({
    metricList,
    healthStats,
    skipPrediction,
    downloadEntity,
    chart,
}) => {
    const page = useEntityPageContext();
    const series = googleSeries({ page });

    return (
        <EntityChartCard
            label="Searches"
            metricList={metricList}
            healthStats={healthStats}
            refOnWrapper
            download={
                <EntityChartDownload
                    as={DownloadTimeSeriesButton}
                    data={series}
                    skipPrediction={skipPrediction}
                    {...(downloadEntity !== undefined && {
                        exportEntity: downloadEntity,
                    })}
                />
            }
        >
            <GoogleEntityChart
                data={series}
                skipPrediction={skipPrediction}
                {...chart}
            />
        </EntityChartCard>
    );
};

const WeeklyChartCard = ({
    label,
    as,
    color,
    seriesExtra,
    contentKey,
    metricList,
    healthStats,
    chart,
}) => {
    const page = useEntityPageContext();
    const series = weeklySeries({ page, color, ...seriesExtra });

    return (
        <EntityChartCard
            label={label}
            metricList={metricList}
            healthStats={healthStats}
            download={
                <EntityChartDownload
                    as={DownloadTimeSeriesButton}
                    data={series}
                    mixpanelMeta={{ contentKey }}
                />
            }
        >
            <WeeklyEntityChart
                as={as}
                data={series}
                seriesQuery={page.seriesQuery}
                {...chart}
            />
        </EntityChartCard>
    );
};

export const InstagramChartCard = (props) => {
    const { theme } = useEntityPageContext();
    return (
        <WeeklyChartCard
            label="Likes"
            as={InstagramTimeSeries}
            color={theme.instagram_orange}
            seriesExtra={{ type: 'likes', metricLabel: 'likes' }}
            contentKey="InstagramTimeSeriesChart"
            {...props}
        />
    );
};

export const TikTokChartCard = (props) => {
    const { theme } = useEntityPageContext();
    return (
        <WeeklyChartCard
            label="Views"
            as={TikTokTimeSeries}
            color={theme.tiktok_pink}
            contentKey="TikTokTimeSeriesChart"
            {...props}
        />
    );
};

export const PopularityChartCard = ({
    metricList,
    healthStats,
    downloadEntity,
    chart,
}) => {
    const page = useEntityPageContext();
    const series = popularitySeries({ page });

    return (
        <EntityChartCard
            label="Popularity"
            metricList={metricList}
            healthStats={healthStats}
            refOnWrapper
            labelExtra={
                <DefinitionTooltip
                    {...tooltips.popularity}
                    week={page.week}
                    year={page.year}
                    mixpanelMeta={{ contentKey: 'popularityOverviewTitle' }}
                    openOnClick={true}
                    icon={<PopularityIcon />}
                    width="22rem"
                />
            }
            download={
                <EntityChartDownload
                    as={DownloadPopularityTimeSeriesButton}
                    data={popularityDownloadSeries({ page })}
                    mixpanelMeta={{ contentKey: 'PopularityTimeSeriesChart' }}
                    {...(downloadEntity !== undefined && {
                        exportEntity: downloadEntity,
                    })}
                />
            }
        >
            <PopularityEntityChart data={series} {...chart} />
        </EntityChartCard>
    );
};
